import React, { useState, useEffect, useMemo, useRef } from "react";
import { createInvoice, updateInvoice } from "../../utils/api.js";
import { Card, Button } from "../../components/legacy-ui.js";
import { formatCurrency } from "../../utils/formatters.js";
import { Plus, Search, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

const fieldClass =
  "w-full p-2.5 bg-white border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";

const matchesQuery = (text, query) =>
  String(text || "")
    .toLowerCase()
    .includes(query);

const productMatchesQuery = (product, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (matchesQuery(product.name, q)) return true;
  return (product.alias || []).some((alias) => matchesQuery(alias, q));
};

const bumpInvoiceNumber = (value, delta) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(.*?)(\d+)(\D*)$/);
  if (!match) {
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return value;
    return String(Math.max(0, numeric + delta));
  }
  const next = Math.max(0, Number(match[2]) + delta);
  return `${match[1]}${String(next).padStart(match[2].length, "0")}${match[3]}`;
};

const buildCartItem = (product, qty, weight, price) => {
  const byWeight = !!product.advanced?.byWeight;
  const item = {
    product: product._id,
    name: product.name,
    price,
    byWeight,
    quantity: qty,
  };
  if (byWeight) {
    item.weight = weight;
    item.total = price * weight;
  } else {
    item.total = price * qty;
  }
  return item;
};

const selectInputValue = (element) => {
  if (!element) return;
  element.focus();
  if (typeof element.select === "function") element.select();
};

const InvoiceForm = ({
  clients,
  products,
  paymentMethods,
  bcvRate,
  nextInvoiceReference,
  recentInvoiceNumbers = [],
  editingInvoice,
  initialData,
  onSave,
  onCancel,
  setInitialData,
}) => {
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [selectedClient, setSelectedClient] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [isClientListOpen, setIsClientListOpen] = useState(false);
  const [clientHighlight, setClientHighlight] = useState(0);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [isIndependentClient, setIsIndependentClient] = useState(false);
  const [cart, setCart] = useState([]);

  const [productSearch, setProductSearch] = useState("");
  const [isProductListOpen, setIsProductListOpen] = useState(false);
  const [productHighlight, setProductHighlight] = useState(0);
  const [currentProduct, setCurrentProduct] = useState("");
  const [currentQty, setCurrentQty] = useState("1");
  const [currentWeight, setCurrentWeight] = useState("");
  const [currentPrice, setCurrentPrice] = useState("");

  const [isPaid, setIsPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentCurrency, setPaymentCurrency] = useState("USD");
  const [paymentReference, setPaymentReference] = useState("");

  const clientSearchRef = useRef(null);
  const clientInputRef = useRef(null);
  const clientListRef = useRef(null);
  const invoiceInputRef = useRef(null);
  const productSearchWrapRef = useRef(null);
  const productInputRef = useRef(null);
  const qtyInputRef = useRef(null);
  const weightInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const saveHandlerRef = useRef(null);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((client) => client.name.toLowerCase().includes(q));
  }, [clients, clientSearch]);

  const filteredProducts = useMemo(
    () => products.filter((product) => productMatchesQuery(product, productSearch)),
    [products, productSearch],
  );

  const selectedProductForForm = products.find((p) => p._id === currentProduct);
  const selectedClientData = clients.find((c) => c._id === selectedClient);
  const invoiceSubtotal = cart.reduce((acc, item) => acc + (Number(item.total) || 0), 0);
  const estimatedCreditApplied =
    !editingInvoice && selectedClientData?.credit > 0
      ? Math.min(invoiceSubtotal, selectedClientData.credit)
      : 0;
  const estimatedPending = Math.max(invoiceSubtotal - estimatedCreditApplied, 0);
  const byWeight = !!selectedProductForForm?.advanced?.byWeight;
  const previewQty = parseFloat(currentQty) || 0;
  const previewWeight = parseFloat(currentWeight) || 0;
  const previewPrice = parseFloat(currentPrice) || 0;
  const previewTotal = selectedProductForForm
    ? byWeight
      ? previewPrice * previewWeight
      : previewPrice * previewQty
    : 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clientSearchRef.current &&
        !clientSearchRef.current.contains(event.target)
      ) {
        setIsClientListOpen(false);
      }
      if (
        productSearchWrapRef.current &&
        !productSearchWrapRef.current.contains(event.target)
      ) {
        setIsProductListOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (paymentMethods.length > 0 && !paymentMethod) {
      setPaymentMethod(paymentMethods[0]._id);
    }
  }, [paymentMethods, paymentMethod]);

  useEffect(() => {
    if (editingInvoice) return;
    if (isIndependentClient) {
      if (nextInvoiceReference) setInvoiceNumber(`i-${nextInvoiceReference}`);
      return;
    }
    setInvoiceNumber((current) => {
      if (current && !String(current).startsWith("i-")) return current;
      return nextInvoiceReference ? String(nextInvoiceReference) : "";
    });
  }, [isIndependentClient, nextInvoiceReference, editingInvoice]);

  useEffect(() => {
    if (editingInvoice) {
      const client = clients.find((c) => c._id === editingInvoice.client._id);
      setSelectedClient(editingInvoice.client._id);
      setClientSearch(client ? client.name : "");
      setInvoiceNumber(editingInvoice.invoiceNumber);
      setIsIndependentClient(
        String(editingInvoice.invoiceNumber || "").startsWith("i-"),
      );
      setCart(editingInvoice.items);
      return;
    }

    if (!initialData || clients.length === 0) return;

    const client = clients.find((c) => c._id === initialData.clientId);
    if (client) {
      setSelectedClient(client._id);
      setClientSearch(client.name);
    }

    const product = products.find((p) => p._id === initialData.productId);
    if (product) {
      const weight = parseFloat(initialData.weight) || 0;
      const qty = parseInt(initialData.quantity, 10) || 1;
      const price =
        initialData.price !== undefined ? initialData.price : product.price;
      const productByWeight = !!product.advanced?.byWeight;

      if (!productByWeight || weight > 0) {
        setCart([buildCartItem(product, qty, weight, price)]);
      } else {
        setCurrentProduct(product._id);
        setCurrentWeight(weight ? String(weight) : "");
        setCurrentPrice(String(price));
        setCurrentQty(String(qty));
      }
    }

    if (setInitialData) setInitialData(null);
  }, [editingInvoice, initialData, clients, products, setInitialData]);

  useEffect(() => {
    if (editingInvoice) return;
    const timer = setTimeout(() => {
      if (initialData?.clientId) {
        selectInputValue(invoiceInputRef.current);
        return;
      }
      clientInputRef.current?.focus();
    }, 40);
    return () => clearTimeout(timer);
    // Solo al abrir el formulario
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setClientHighlight(0);
  }, [clientSearch]);

  useEffect(() => {
    setProductHighlight(0);
  }, [productSearch]);

  useEffect(() => {
    const el = clientListRef.current?.querySelector(
      `[data-client-idx="${clientHighlight}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [clientHighlight]);

  const focusPrimaryProductField = (product = selectedProductForForm) => {
    requestAnimationFrame(() => {
      const el = product?.advanced?.byWeight
        ? weightInputRef.current
        : qtyInputRef.current;
      selectInputValue(el);
    });
  };

  const handleProductSelect = (productId) => {
    setCurrentProduct(productId);
    const prod = products.find((p) => p._id === productId);
    setIsProductListOpen(false);
    setProductSearch("");
    if (prod) {
      setCurrentPrice(String(prod.price ?? ""));
      setCurrentQty("1");
      setCurrentWeight("");
      focusPrimaryProductField(prod);
    } else {
      setCurrentPrice("");
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client._id);
    setClientSearch(client.name);
    setIsClientListOpen(false);
    requestAnimationFrame(() => selectInputValue(invoiceInputRef.current));
  };

  const addToCart = () => {
    const product = products.find((p) => p._id === currentProduct);
    if (!product) {
      productInputRef.current?.focus();
      return false;
    }
    const qty = parseFloat(currentQty) || 0;
    const weight = parseFloat(currentWeight) || 0;
    const price = parseFloat(currentPrice) || 0;
    if (product.advanced?.byWeight) {
      if (weight <= 0) {
        toast.error("Para productos por peso, el peso debe ser mayor a 0.", {
          position: "top-center",
        });
        weightInputRef.current?.focus();
        return false;
      }
    } else if (qty <= 0) {
      toast.error("La cantidad debe ser mayor a 0.", {
        position: "top-center",
      });
      qtyInputRef.current?.focus();
      return false;
    }

    setCart((prev) => [...prev, buildCartItem(product, qty, weight, price)]);
    setCurrentQty("1");
    setCurrentWeight("");
    focusPrimaryProductField(product);
    return true;
  };

  const removeFromCart = (idx) =>
    setCart((prev) => prev.filter((_, i) => i !== idx));

  const updateCartItem = (idx, patch) => {
    setCart((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        const next = { ...item, ...patch };
        const qty = parseFloat(next.quantity) || 0;
        const weight = parseFloat(next.weight) || 0;
        const price = parseFloat(next.price) || 0;
        next.total = next.byWeight ? price * weight : price * qty;
        return next;
      }),
    );
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!selectedClient || cart.length === 0 || !invoiceNumber) {
      const msg =
        "Por favor, complete todos los campos: cliente, número de factura y al menos un producto.";
      setSaveError(msg);
      toast.error(msg, { position: "top-center" });
      if (!selectedClient) clientInputRef.current?.focus();
      else if (!invoiceNumber) invoiceInputRef.current?.focus();
      return;
    }

    const invoiceData = {
      client: selectedClient,
      invoiceNumber: invoiceNumber.trim(),
      items: cart,
      totalAmount: invoiceSubtotal,
    };

    try {
      setIsSaving(true);
      let savedInvoice;
      if (editingInvoice) {
        const res = await updateInvoice(editingInvoice._id, invoiceData);
        savedInvoice = res.data.data;
      } else {
        if (isPaid && !paymentMethod) {
          const msg = "Por favor, seleccione un método de pago.";
          setSaveError(msg);
          toast.error(msg, { position: "top-center" });
          return;
        }
        if (isPaid) {
          invoiceData.payment = {
            method: paymentMethod,
            currency: paymentCurrency,
            reference: paymentReference,
          };
        }
        const res = await createInvoice(invoiceData);
        savedInvoice = res.data.data;
      }
      setSaveError("");
      toast.success(editingInvoice ? "Factura actualizada" : "Factura creada", {
        position: "top-center",
      });
      onSave(savedInvoice);
    } catch (err) {
      console.error("Error saving invoice", err);
      const backendMsg =
        err.response?.data?.error ||
        err.message ||
        "Error desconocido al guardar la factura.";
      setSaveError(backendMsg);
      toast.error(backendMsg, { position: "top-center" });
    } finally {
      setIsSaving(false);
    }
  };

  saveHandlerRef.current = handleSave;

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        event.preventDefault();
        saveHandlerRef.current?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleClientKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsClientListOpen(true);
      setClientHighlight((i) =>
        Math.min(i + 1, Math.max(filteredClients.length - 1, 0)),
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setClientHighlight((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const pick =
        filteredClients[clientHighlight] ||
        (filteredClients.length === 1 ? filteredClients[0] : null);
      if (pick) {
        handleClientSelect(pick);
        return;
      }
      if (selectedClient) selectInputValue(invoiceInputRef.current);
      return;
    }
    if (event.key === "Escape") setIsClientListOpen(false);
  };

  const handleInvoiceKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      productInputRef.current?.focus();
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setInvoiceNumber((current) => bumpInvoiceNumber(current, 1));
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setInvoiceNumber((current) => bumpInvoiceNumber(current, -1));
    }
  };

  const handleProductSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsProductListOpen(true);
      setProductHighlight((i) =>
        Math.min(i + 1, Math.max(filteredProducts.length - 1, 0)),
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setProductHighlight((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (currentProduct && !productSearch) {
        focusPrimaryProductField();
        return;
      }
      const pick =
        filteredProducts.length === 1
          ? filteredProducts[0]
          : productSearch
            ? filteredProducts[productHighlight]
            : null;
      if (pick) handleProductSelect(pick._id);
      return;
    }
    if (event.key === "Escape") setIsProductListOpen(false);
  };

  const handleAddFieldKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addToCart();
    }
  };

  const canSave = selectedClient && cart.length > 0 && invoiceNumber && !isSaving;

  return (
    <div className="space-y-4 p-4 sm:p-6 h-full overflow-y-auto pb-24">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="secondary" onClick={onCancel}>
            ← Volver
          </Button>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 truncate">
            {editingInvoice ? "Editar Factura" : "Nueva Factura"}
          </h2>
        </div>
        <p className="hidden sm:block text-xs text-gray-400 shrink-0">
          Enter agrega · Ctrl+Enter guarda
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-3">
              <div className="relative" ref={clientSearchRef}>
                <label className="text-sm font-medium text-gray-600 block mb-1">
                  Cliente
                </label>
                <div className="relative">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    ref={clientInputRef}
                    type="text"
                    autoComplete="off"
                    placeholder="Escriba y pulse Enter..."
                    className={`${fieldClass} pl-9`}
                    value={clientSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setClientSearch(value);
                      setIsClientListOpen(true);
                      if (
                        selectedClientData &&
                        value.trim().toLowerCase() !==
                          selectedClientData.name.toLowerCase()
                      ) {
                        setSelectedClient("");
                      }
                    }}
                    onFocus={() => setIsClientListOpen(true)}
                    onKeyDown={handleClientKeyDown}
                  />
                </div>
                {isClientListOpen && (
                  <div
                    ref={clientListRef}
                    className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-56 overflow-y-auto"
                  >
                    {filteredClients.length === 0 ? (
                      <div className="p-3 text-sm text-gray-400">
                        Sin clientes
                      </div>
                    ) : (
                      filteredClients.map((client, idx) => (
                        <button
                          type="button"
                          key={client._id}
                          data-client-idx={idx}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            idx === clientHighlight
                              ? "bg-indigo-50 text-indigo-800"
                              : "hover:bg-gray-50"
                          } ${
                            selectedClient === client._id ? "font-semibold" : ""
                          }`}
                          onMouseEnter={() => setClientHighlight(idx)}
                          onClick={() => handleClientSelect(client)}
                        >
                          <span>{client.name}</span>
                          {client.credit > 0 && (
                            <span className="ml-2 text-xs text-emerald-600">
                              créd. {formatCurrency(client.credit)}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">
                  Número de Factura
                </label>
                <div className="flex gap-1.5">
                  <input
                    ref={invoiceInputRef}
                    type="text"
                    autoComplete="off"
                    placeholder={
                      isIndependentClient && nextInvoiceReference
                        ? `i-${nextInvoiceReference}`
                        : "Ej: 001-000123 o FAC-12"
                    }
                    className={fieldClass}
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    onKeyDown={handleInvoiceKeyDown}
                  />
                  {!editingInvoice && !isIndependentClient && (
                    <div className="flex flex-col shrink-0">
                      <button
                        type="button"
                        className="px-2 h-1/2 text-xs font-bold text-gray-600 border border-gray-300 rounded-t-md hover:bg-gray-50"
                        onClick={() =>
                          setInvoiceNumber((current) =>
                            bumpInvoiceNumber(current || nextInvoiceReference, 1),
                          )
                        }
                      >
                        +
                      </button>
                      <button
                        type="button"
                        className="px-2 h-1/2 text-xs font-bold text-gray-600 border border-t-0 border-gray-300 rounded-b-md hover:bg-gray-50"
                        onClick={() =>
                          setInvoiceNumber((current) =>
                            bumpInvoiceNumber(current || nextInvoiceReference, -1),
                          )
                        }
                      >
                        −
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {!editingInvoice && (
                    <label className="inline-flex items-center gap-1.5 cursor-pointer mr-1">
                      <input
                        type="checkbox"
                        checked={isIndependentClient}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsIndependentClient(checked);
                          if (!checked) {
                            setInvoiceNumber(
                              nextInvoiceReference
                                ? String(nextInvoiceReference)
                                : "",
                            );
                          }
                        }}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-xs text-gray-600">Independiente</span>
                    </label>
                  )}
                  {nextInvoiceReference && !isIndependentClient && (
                    <button
                      type="button"
                      className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                      onClick={() =>
                        setInvoiceNumber(String(nextInvoiceReference))
                      }
                    >
                      Siguiente: {nextInvoiceReference}
                    </button>
                  )}
                  {!editingInvoice &&
                    recentInvoiceNumbers.map((number) => (
                      <button
                        type="button"
                        key={number}
                        className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                        onClick={() => setInvoiceNumber(String(number))}
                        title="Usar este número"
                      >
                        {number}
                      </button>
                    ))}
                </div>
              </div>
            </div>

            {selectedClientData && (
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-md bg-gray-50 border border-gray-200 text-gray-600">
                  Pedidos:{" "}
                  <strong className="text-gray-800">
                    {selectedClientData.orderCount || 0}
                  </strong>
                </span>
                <span className="px-2 py-1 rounded-md bg-emerald-50 border border-emerald-100 text-emerald-700">
                  Crédito:{" "}
                  <strong>
                    {formatCurrency(selectedClientData.credit || 0)}
                  </strong>
                </span>
              </div>
            )}
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Productos</h3>
              {selectedProductForForm && previewTotal > 0 && (
                <span className="text-sm text-indigo-700 font-semibold">
                  Línea: {formatCurrency(previewTotal)}
                </span>
              )}
            </div>

            <div className="relative mb-3" ref={productSearchWrapRef}>
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                ref={productInputRef}
                type="text"
                autoComplete="off"
                placeholder="Buscar producto o alias..."
                className={`${fieldClass} pl-9`}
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setIsProductListOpen(true);
                }}
                onFocus={() => setIsProductListOpen(true)}
                onKeyDown={handleProductSearchKeyDown}
              />
              {isProductListOpen && productSearch && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredProducts.length === 0 ? (
                    <div className="p-3 text-sm text-gray-400">
                      Sin productos
                    </div>
                  ) : (
                    filteredProducts.map((product, idx) => (
                      <button
                        type="button"
                        key={product._id}
                        className={`w-full text-left px-3 py-2 text-sm flex justify-between gap-3 ${
                          idx === productHighlight
                            ? "bg-indigo-50 text-indigo-800"
                            : "hover:bg-gray-50"
                        }`}
                        onMouseEnter={() => setProductHighlight(idx)}
                        onClick={() => handleProductSelect(product._id)}
                      >
                        <span>{product.name}</span>
                        <span className="text-gray-500 shrink-0">
                          {formatCurrency(product.price)}
                          {product.advanced?.byWeight ? "/kg" : ""}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {products.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {products.map((product) => {
                  const active = currentProduct === product._id;
                  return (
                    <button
                      type="button"
                      key={product._id}
                      onClick={() => handleProductSelect(product._id)}
                      className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:text-indigo-700"
                      }`}
                    >
                      {product.name}
                    </button>
                  );
                })}
              </div>
            )}

            {selectedProductForForm && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                {byWeight ? (
                  <>
                    <div>
                      <label className="text-xs font-medium text-gray-600">
                        Panelas
                      </label>
                      <input
                        ref={qtyInputRef}
                        type="number"
                        inputMode="decimal"
                        className={fieldClass}
                        value={currentQty}
                        onChange={(e) => setCurrentQty(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={handleAddFieldKeyDown}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600">
                        Peso (kg)
                      </label>
                      <input
                        ref={weightInputRef}
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        placeholder="15.2"
                        className={fieldClass}
                        value={currentWeight}
                        onChange={(e) => setCurrentWeight(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={handleAddFieldKeyDown}
                      />
                    </div>
                  </>
                ) : (
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-gray-600">
                      Cantidad
                    </label>
                    <input
                      ref={qtyInputRef}
                      type="number"
                      inputMode="decimal"
                      className={fieldClass}
                      value={currentQty}
                      onChange={(e) => setCurrentQty(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={handleAddFieldKeyDown}
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-gray-600">
                    {byWeight ? "Precio/kg" : "Precio/u"}
                  </label>
                  <input
                    ref={priceInputRef}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    className={fieldClass}
                    value={currentPrice}
                    onChange={(e) => setCurrentPrice(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    onKeyDown={handleAddFieldKeyDown}
                  />
                </div>
                <Button
                  onClick={addToCart}
                  disabled={!currentProduct}
                  className="w-full"
                >
                  <Plus size={16} />
                  Agregar
                </Button>
              </div>
            )}
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 font-semibold text-sm">
              Detalle ({cart.length})
            </div>
            <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {cart.length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">
                  Seleccione un producto, capture peso o cantidad y pulse Enter
                </div>
              ) : (
                cart.map((item, idx) => (
                  <div
                    key={`${item.product}-${idx}`}
                    className="p-3 flex flex-wrap items-center gap-3"
                  >
                    <div className="min-w-[8rem] flex-1">
                      <p className="font-medium text-gray-800">{item.name}</p>
                      <p className="text-xs text-gray-500">
                        {item.byWeight ? "Por peso" : "Por unidad"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <label className="text-xs text-gray-500">
                        {item.byWeight ? "Pnl" : "Cant"}
                        <input
                          type="number"
                          className="block w-16 mt-0.5 p-1.5 border border-gray-300 rounded-md text-sm"
                          value={item.quantity}
                          onChange={(e) =>
                            updateCartItem(idx, {
                              quantity: parseFloat(e.target.value) || 0,
                            })
                          }
                          onFocus={(e) => e.target.select()}
                        />
                      </label>
                      {item.byWeight && (
                        <label className="text-xs text-gray-500">
                          Kg
                          <input
                            type="number"
                            step="0.01"
                            className="block w-20 mt-0.5 p-1.5 border border-gray-300 rounded-md text-sm"
                            value={item.weight}
                            onChange={(e) =>
                              updateCartItem(idx, {
                                weight: parseFloat(e.target.value) || 0,
                              })
                            }
                            onFocus={(e) => e.target.select()}
                          />
                        </label>
                      )}
                      <label className="text-xs text-gray-500">
                        Precio
                        <input
                          type="number"
                          step="0.01"
                          className="block w-20 mt-0.5 p-1.5 border border-gray-300 rounded-md text-sm"
                          value={item.price}
                          onChange={(e) =>
                            updateCartItem(idx, {
                              price: parseFloat(e.target.value) || 0,
                            })
                          }
                          onFocus={(e) => e.target.select()}
                        />
                      </label>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <span className="font-bold">
                        {formatCurrency(item.total)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFromCart(idx)}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card className="p-5 sticky top-4">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Resumen</h3>
            <div className="space-y-2 mb-3 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Items</span>
                <span>{cart.length}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatCurrency(invoiceSubtotal)}</span>
              </div>
              {!editingInvoice && selectedClientData && (
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>Crédito disponible</span>
                    <span>
                      {formatCurrency(selectedClientData.credit || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-emerald-600">
                    <span>Crédito aplicado</span>
                    <span>- {formatCurrency(estimatedCreditApplied)}</span>
                  </div>
                </>
              )}
            </div>
            <div className="border-t border-gray-200 pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-indigo-600">
                  {formatCurrency(invoiceSubtotal)}
                </span>
              </div>
              {!editingInvoice && selectedClientData && (
                <div className="flex justify-between items-center text-sm font-semibold">
                  <span className="text-gray-700">Pendiente estimado</span>
                  <span className="text-red-500">
                    {formatCurrency(estimatedPending)}
                  </span>
                </div>
              )}
              {!editingInvoice && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPaid}
                      onChange={() => setIsPaid(!isPaid)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 font-semibold text-gray-700">
                      Registrar pago inmediato
                    </span>
                  </label>
                  {isPaid && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-1">
                          Método de Pago
                        </label>
                        <select
                          className={fieldClass}
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        >
                          {paymentMethods.map((method) => (
                            <option key={method._id} value={method._id}>
                              {method.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-1">
                          Moneda
                        </label>
                        <select
                          className={fieldClass}
                          value={paymentCurrency}
                          onChange={(e) => setPaymentCurrency(e.target.value)}
                        >
                          <option value="USD">USD</option>
                          <option value="BS">BS</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 block mb-1">
                          Referencia
                        </label>
                        <input
                          type="text"
                          placeholder="Nro. de referencia"
                          className={fieldClass}
                          value={paymentReference}
                          onChange={(e) => setPaymentReference(e.target.value)}
                        />
                      </div>
                      {paymentCurrency === "BS" && bcvRate && (
                        <div className="text-sm text-gray-600 p-2 bg-indigo-50 rounded-md border border-indigo-100">
                          <p>
                            Total (aprox):{" "}
                            <span className="font-bold">
                              {formatCurrency(
                                invoiceSubtotal * bcvRate.rate,
                                "BS",
                              )}
                            </span>
                          </p>
                          <p className="text-xs text-gray-500">
                            Tasa: {bcvRate.rate}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Button
                  className="w-full py-3 text-lg"
                  onClick={handleSave}
                  disabled={!canSave}
                >
                  {isSaving
                    ? "Guardando..."
                    : editingInvoice
                      ? "Guardar Cambios"
                      : "Generar Factura"}
                </Button>
                {saveError && (
                  <div className="text-xs sm:text-sm text-red-600 text-center">
                    {saveError}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InvoiceForm;
